import type { LegacyNotificationUserSubscriptionHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.notifications";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

export class LegacyNotificationUserSubscriptionVoter extends ContextVoter implements ContextVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    // If creating a subscription for the current user, allow access
    if (item) {
      const subscription = item as LegacyNotificationUserSubscriptionHydraItem;
      if (subscription?.subscriber === this.permissionService.userIri) {
        return true;
      }
    }
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionCreateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionCreate,
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    // If the subscription belongs to the current user, allow access
    const subscription = item as LegacyNotificationUserSubscriptionHydraItem;
    if (subscription?.subscriber === this.permissionService.userIri) {
      return true;
    }
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionReadany,
      contextAttribute: PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionRead,
      scope: PermissionScope.PROPERTY,
    });
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    // If the subscription belongs to the current user, allow access
    const subscription = item as LegacyNotificationUserSubscriptionHydraItem;
    if (subscription?.subscriber === this.permissionService.userIri) {
      return true;
    }
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionDeleteany,
      contextAttribute: PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionDelete,
      scope: PermissionScope.PROPERTY,
    });
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    if (item?.location === "/api/locations/2") {
      return false;
    }
    // If the subscription belongs to the current user, allow access
    const subscription = item as LegacyNotificationUserSubscriptionHydraItem;
    if (subscription?.subscriber === this.permissionService.userIri) {
      return true;
    }
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionUpdateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionUpdate,
      scope: PermissionScope.PROPERTY,
    });
  };

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionCreateany,
        PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionUpdateany,
        PermissionAttributesEnum.GlobalOperationLegacynotificationusersubscriptionDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionCreate,
        PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionUpdate,
        PermissionAttributesEnum.ContextOperationLegacynotificationusersubscriptionDelete,
      ],
      scope: PermissionScope.PROPERTY,
    });
  };
}
